import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useRef, useEffect } from 'react';
import SignaturePad from 'react-signature-canvas';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { useManageSignatureModalStore } from './ManageSignatureModalStore';

import './SignatureModal.scss';
import { MLButton } from 'components/Button/Button';
import usePutchCustomerSignature from 'hooks/VisitReport/usePutchCustomerSignature';
import usePutchPestUserSignature from 'hooks/VisitReport/usePutchPestUserSignature';

interface SignatureModalProps {
  refetch: () => void;
}

const SignatureModal: React.FC<SignatureModalProps> = ({ refetch }) => {
  const { putchCustomerSignature } = usePutchCustomerSignature();
  const { putchPestUserSignature } = usePutchPestUserSignature();
  const { isOpen, slug, visitReportId, isCustomer, onManageSignatureModalClose, documentType } =
    useManageSignatureModalStore();
  const sigCanvas = useRef<SignaturePad | null>(null);

  const addCustomerSignature = () => {
    if (!sigCanvas.current) {
      console.warn('Signature pad is not initialized');
      return;
    }

    if (sigCanvas.current.isEmpty()) {
      enqueueSnackbar('Signature pad is empty', { variant: 'warning' });
      return;
    }

    const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');

    putchCustomerSignature(
      { slug, visitReportId, signatureData, documentType },
      {
        onSuccess: () => {
          enqueueSnackbar('Signature seted', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          refetch();
          onManageSignatureModalClose();
        },
      },
    );
  };

  const addPestUserSignature = () => {
    if (!sigCanvas.current) {
      console.warn('Signature pad is not initialized');
      return;
    }

    if (sigCanvas.current.isEmpty()) {
      enqueueSnackbar('Signature pad is empty', { variant: 'warning' });
      return;
    }

    const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');

    putchPestUserSignature(
      { visitReportId, signatureData },
      {
        onSuccess: () => {
          enqueueSnackbar('Your signature is updated', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          refetch();
          onManageSignatureModalClose();
        },
      },
    );
  };

  useEffect(() => {
    if (isOpen && sigCanvas.current) {
      sigCanvas.current.clear();
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={onManageSignatureModalClose} size="xl">
      <ModalHeader className="border-bottom pb-2 pt-3 mx-3" toggle={onManageSignatureModalClose}>
        Change signature
      </ModalHeader>
      <ModalBody className="signature-modal-body">
        <div className="signature-wrapper">
          <SignaturePad ref={sigCanvas} canvasProps={{ className: 'signatureCanvas' }} />
        </div>
      </ModalBody>
      <ModalFooter>
        <MLButton isGhost color="warning" onClick={() => sigCanvas.current?.clear()} innerText="Clear" fontSize={16} />
        <MLButton
          isGhost
          color="success"
          onClick={isCustomer ? addCustomerSignature : addPestUserSignature}
          innerText="Save"
          fontSize={16}
        />
      </ModalFooter>
    </Modal>
  );
};

export default SignatureModal;
